import { defineAsyncComponent } from 'vue';
import { useNuxtApp } from '#app';
import { useProductQVStore } from '@nuxt-common/product-quickview/app/stores/useProductQVStore';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { usePageData } from '~/stores/pageData';
import { onKsFlagsReady } from '~/events';
import useKillswitches from '~/composables/useKillswitches';
import type { AgentEventDetail, Product } from '~/types/ask-ai';

export function getColorId(uri = ''): string | undefined {
  const queryString = uri.includes('?') ? uri.split('?')[1] : uri;
  const params = new URLSearchParams(queryString);
  return params.get('swatchColor') || undefined;
}

export const useAskAIQuickView = () => {
  const killswitches = useKillswitches();
  const pqvComponent = shallowRef(null);

  const pageData = usePageData();
  const nuxtApp = useNuxtApp();

  const ctx = computed(() => pageData.navigation.context);

  const productQVStore = useProductQVStore();
  const slideoutStore = useSlideoutStore();

  const productQuickviewProps = computed(() => ({
    pageType: 'ask ai navigation', // TODO change type
    isBuyNowCtaEnabled: false,
    isPricingSimplification: true,
    atbSource: 'ASKA2B',

    // tagging
    tagging: {
      onDrawerLoad: {
        quick_view_type: 'ask ai quick view',
      },
      onProductDetailClick: {
        event_name: 'ask ai pqv:view details',
        link_name: 'ask ai pqv:view details',
      },
      onAddToBagRequest: {
        bag_add_location: 'ask ai pqv',
      },
    },
  }));

  const pqvEnabled = ref(false);
  const initPQV = () => {
    if (pqvEnabled.value) {
      pqvComponent.value = defineAsyncComponent(
        () => import(/* webpackChunkName: 'ProductQuickviewSlideout' */ '@nuxt-common/product-quickview/app/components/ProductQuickviewSlideout'),
      );
    }
  };

  const isPQVinitilized = computed(() => !!pqvComponent.value);

  const openSlideoutPQV = (product: Product) => {
    if (productQVStore && slideoutStore) {
      const productId = String(product?.id || '');
      if (!productId) {
        return;
      }

      // Guard against rapid duplicate clicks/events triggering back-to-back toggles (fixes ask-ai double-click issue)
      if (productQVStore.isFetching || (productQVStore.productQvSlideoutOpen && productQVStore.activeProductId === productId)) {
        return;
      }

      const navigationContext = {
        regionCode: ctx.value?.regionCode,
        currencyCode: ctx.value?.currencyCode,
      };

      productQVStore.toggle({
        productId,
        slideoutTitle: 'Quick Shop',
        swatchColor: getColorId(product.uri),
        navigationContext,
      });
    }
  };

  if (import.meta.client) {
    onKsFlagsReady(() => {
      pqvEnabled.value = killswitches.value?.aiQuickShopEnabled;
    });
  }

  nuxtApp.hook('ask-ai:google-agent-single-item-clicked', ({ product, source }: AgentEventDetail) => {
    if (!window.isAskAiWindowOpen) return;

    if (source && source !== 'EVENT_SOURCE_AGENT') {
      return;
    }

    if (pqvEnabled.value && product?.uriDisabled) {
      openSlideoutPQV(product);
    } else if (product?.uri) {
      window.location.href = product.uri; // manual navigation if pqv is disabled regardless of a[href] tag presence
    }
  });

  nuxtApp.hook('ask-ai:google-agent-open-changed', (detail) => {
    window.isAskAiWindowOpen = detail.isOpen;
    if (detail.isOpen && !isPQVinitilized.value) {
      initPQV();
    }
  });

  return {
    initPQV,
    pqvComponent,
    productQuickviewProps,
  };
};
